import {
  Button,
  DropDownMenu,
  styled,
  Switch,
} from "@monolith-forensics/monolith-ui";
import {
  ArrowLeftIcon,
  EditIcon,
  MoreHorizontalIcon,
  Trash2Icon,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { useState } from "react";
import { DeleteWebhookModal, WebhookEventDefinitions } from "@/Features";
import {
  useGetWebhook,
  useUpdateWebhook,
} from "@/Features/Webhooks/webhooks.query";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 20px;
  max-width: 700px;
  gap: 20px;
`;

const PageTitle = styled.h2``;

const StyledDescription = styled.p`
  margin: 0px;
  font-size: 12px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const RowItemContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
`;

const ListContainer = styled.div`
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 8px;

  background-color: ${(props) => props.theme.palette.background.default};

  min-height: 50px;

  ${RowItemContent} {
    border-bottom: 1px solid ${(props) => props.theme.palette.divider};
    &:last-child {
      border-bottom: none;
    }
  }
`;

const Detail = styled.div``;

const Label = styled.div`
  user-select: none;

  color: ${(props) => props.theme.palette.text.primary};
  font-weight: 600;
  font-size: 12px;
`;

const Value = styled.div`
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: 12px;

  &.status {
    font-weight: 600;
  }

  &[data-enabled="true"] {
    color: limegreen;
  }

  &[data-enabled="false"] {
    color: orangered;
  }
`;

export const WebhookDetailsPage = () => {
  const { currentUser, hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { uuid } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutate: updateWebhook } = useUpdateWebhook();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading } = useGetWebhook({ uuid });

  const webhook = data?.data?.[0];

  const handleUpdateStatus = (status: boolean) => {
    if (uuid && webhook) {
      updateWebhook({
        uuid,
        enabled: status,
      });

      LoggingAPI.createLog({
        case_id: undefined,
        user_id: currentUser.user_id,
        message: `Updated webhook: ${webhook.name}`,
        event: "webhook:update",
        object_type: "webhook",
        object_uuid: uuid,
      });
    }
  };

  if (isLoading) return null;

  return (
    <StyledContent>
      <Link to={"/settings/api"}>
        <Button
          size="xs"
          variant="text"
          leftSection={<ArrowLeftIcon size={16} />}
          style={{ color: "grey" }}
        >
          API Settings
        </Button>
      </Link>
      {!webhook ? (
        <PageTitle>Webhook not found</PageTitle>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <PageTitle>{webhook?.name}</PageTitle>
              <StyledDescription>
                Created {moment(webhook?.created_on).format("MMMM D, YYYY")} by{" "}
                {webhook?.created_by?.full_name} ({webhook?.created_by?.email})
              </StyledDescription>
            </div>
            <DropDownMenu
              variant="subtle"
              size="xs"
              buttonProps={{
                style: { padding: "0px 4px" },
              }}
              data={[
                {
                  label: "Edit Webhook",
                  value: "edit",
                  leftSection: <EditIcon size={14} />,
                  disabled: !hasPermission(
                    MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE
                  ),
                  onClick: () => {
                    navigate(`/settings/api/webhooks/${webhook?.uuid}/edit`);
                  },
                },
                {
                  label: "Delete Webhook",
                  value: "delete",
                  leftSection: <Trash2Icon size={14} color="orangered" />,
                  disabled: !hasPermission(
                    MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE
                  ),
                  onClick: () => {
                    setShowDeleteModal(true);
                  },
                },
              ]}
            >
              <MoreHorizontalIcon size={16} />
            </DropDownMenu>
            <DeleteWebhookModal
              show={showDeleteModal}
              onClose={() => {
                setShowDeleteModal(false);
              }}
              onSubmit={() => {
                setShowDeleteModal(false);
                enqueueSnackbar("Webhook deleted successfully", {
                  variant: "success",
                });
                navigate("/settings/api");
              }}
              onCancel={() => {
                setShowDeleteModal(false);
              }}
              defaultFormData={webhook}
            />
          </div>
          <ListContainer>
            <RowItemContent>
              <Detail>
                <Label>Status</Label>
                <Value className="status" data-enabled={!!webhook?.enabled}>
                  {webhook?.enabled ? "Enabled" : "Disabled"}
                </Value>
              </Detail>
              <Switch
                defaultValue={webhook?.enabled}
                onChange={handleUpdateStatus}
                disabled={
                  !hasPermission(MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE)
                }
              />
            </RowItemContent>
            <RowItemContent>
              <Detail>
                <Label>URL</Label>
                <Value>{webhook?.url}</Value>
              </Detail>
            </RowItemContent>
            <RowItemContent>
              <Detail>
                <Label>Description</Label>
                <Value>{webhook?.description}</Value>
              </Detail>
            </RowItemContent>
            <RowItemContent>
              <Detail>
                <Label>Signing Secret</Label>
                <Value>{webhook?.secret}</Value>
              </Detail>
            </RowItemContent>
            <RowItemContent>
              <Detail>
                <Label>UUID</Label>
                <Value>{webhook?.uuid}</Value>
              </Detail>
            </RowItemContent>
            <RowItemContent>
              <Detail>
                <Label>Events</Label>
                <Value>
                  {WebhookEventDefinitions.filter((e) =>
                    webhook?.events.includes(e.value)
                  )
                    .map((e) => e.label)
                    .join(", ")}
                </Value>
              </Detail>
            </RowItemContent>
          </ListContainer>
        </>
      )}
    </StyledContent>
  );
};
